import React from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { CircularProgress } from '../CircularProgress';
import { twMerge } from 'tailwind-merge';

export interface IconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** The variant of the button */
  variant?: 'solid' | 'outlined' | 'plain' | 'soft';
  /** The color scheme of the button */
  color?: 'primary' | 'neutral' | 'danger' | 'success' | 'warning';
  /** The size of the button */
  size?: 'sm' | 'md' | 'lg';
  /** Whether the button is in a loading state */
  loading?: boolean;
  /** Whether the button should be circular */
  circular?: boolean;
  /** The icon to display */
  icon: IconProp;
  /** Additional className for the button */
  className?: string;
  /** SX props for the button */
  sx?: React.CSSProperties;
}

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      variant = 'plain',
      color = 'neutral',
      size = 'md',
      loading = false,
      circular = false,
      icon,
      disabled,
      className = '',
      sx = {},
      onClick,
      ...props
    },
    ref
  ) => {
    const baseStyles = twMerge(
      'inline-flex items-center justify-center transition-all duration-200 select-none rounded-md flex-shrink-0',
      circular && 'rounded-full aspect-square'
    );

    const sizeStyles = {
      sm: circular ? 'w-8' : 'w-8 h-8 px-1.5',
      md: circular ? 'w-10' : 'w-10 h-10 px-2',
      lg: circular ? 'w-12' : 'w-12 h-12 px-2.5',
    };

    const iconSizeStyles = {
      sm: 'sm' as const,
      md: 'sm' as const,
      lg: 'sm' as const,
    };

    const variantStyles = {
      solid: {
        primary: 'bg-sky-500 text-white hover:bg-sky-600 active:bg-sky-600',
        neutral:
          'bg-neutral-800 text-white hover:bg-neutral-900 active:bg-neutral-950',
        danger: 'bg-red-500 text-white hover:bg-red-600 active:bg-red-700',
        success:
          'bg-green-600 text-white hover:bg-green-700 active:bg-green-800',
        warning:
          'bg-yellow-500 text-white hover:bg-yellow-600 active:bg-yellow-700',
      },
      outlined: {
        primary:
          'border border-sky-300 text-sky-600 hover:bg-sky-50 active:bg-sky-100',
        neutral:
          'border border-neutral-300 text-neutral-700 hover:bg-neutral-50 active:bg-neutral-100',
        danger:
          'border border-red-300 text-red-700 hover:bg-red-50 active:bg-red-100',
        success:
          'border border-green-300 text-green-700 hover:bg-green-50 active:bg-green-100',
        warning:
          'border border-yellow-300 text-yellow-700 hover:bg-yellow-50 active:bg-yellow-100',
      },
      plain: {
        primary: 'text-sky-600 hover:bg-sky-50 active:bg-sky-100',
        neutral: 'text-neutral-700 hover:bg-neutral-50 active:bg-neutral-100',
        danger: 'text-red-700 hover:bg-red-50 active:bg-red-100',
        success: 'text-green-700 hover:bg-green-50 active:bg-green-100',
        warning: 'text-yellow-700 hover:bg-yellow-50 active:bg-yellow-100',
      },
      soft: {
        primary: 'bg-sky-50 text-sky-600 hover:bg-sky-100 active:bg-sky-200',
        neutral:
          'bg-neutral-50 text-neutral-700 hover:bg-neutral-100 active:bg-neutral-200',
        danger: 'bg-red-50 text-red-700 hover:bg-red-100 active:bg-red-200',
        success:
          'bg-green-50 text-green-700 hover:bg-green-100 active:bg-green-200',
        warning:
          'bg-yellow-50 text-yellow-700 hover:bg-yellow-100 active:bg-yellow-200',
      },
    };

    const disabledStyles = {
      solid: 'disabled:bg-neutral-100 disabled:text-neutral-400',
      outlined:
        'disabled:border-neutral-200 disabled:text-neutral-400 disabled:bg-transparent',
      plain: 'disabled:text-neutral-400 disabled:bg-transparent',
      soft: 'disabled:bg-neutral-100 disabled:text-neutral-400',
    };

    const buttonClasses = twMerge(
      baseStyles,
      sizeStyles[size],
      variantStyles[variant][color],
      disabledStyles[variant],
      'relative',
      className
    );

    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={buttonClasses}
        style={sx}
        onClick={onClick}
        data-testid="icon-button"
        {...props}
      >
        {loading ? (
          <CircularProgress
            size="sm"
            color="primary"
            data-testid="icon-button-loading"
          />
        ) : (
          <Icon
            icon={icon}
            size={iconSizeStyles[size]}
            data-testid="icon-button-icon"
          />
        )}
      </button>
    );
  }
);

IconButton.displayName = 'IconButton';

export default IconButton;
